import React, { useEffect, useState, Suspense, startTransition } from "react";
import { Canvas, useLoader, extend } from "@react-three/fiber";
import { TextureLoader, SphereGeometry } from "three";
import { OrbitControls, Sky } from "@react-three/drei";
import { PanoramaViewerProps, ThumbnailProps } from "../../Types/types";
import { useTheme, useMediaQuery } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

extend({ SphereGeometry });

const Thumbnail: React.FC<ThumbnailProps> = ({ path, angle, onClick }) => {
  const radius = 50;
  const radian = (angle * Math.PI) / 180;
  const position: [number, number, number] = [
    radius * Math.cos(radian),
    0,
    radius * Math.sin(radian),
  ];

  return (
    <mesh position={position} onClick={() => onClick(path)}>
      <sphereGeometry attach="geometry" args={[1, 32, 32]} />
      <meshBasicMaterial attach="material" color="blue" />
    </mesh>
  );
};

const PanoramaViewer: React.FC<
  PanoramaViewerProps & {
    setIsFullscreen: (isFullscreen: boolean) => void;
    stepId: number;
    prefetchedContent: { [key: number]: string };
    handleStepUpdate: (newStepId: number) => void;
  }
> = ({
  initialImagePath,
  clickToPaths,
  onThumbnailClick,
  setIsFullscreen,
  stepId,
  prefetchedContent,
  handleStepUpdate,
}) => {
  const [currentImagePath, setCurrentImagePath] = useState(initialImagePath);
  const [currentPanoramaId, setCurrentPanoramaId] = useState(stepId);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const [isFullscreen, setLocalIsFullscreen] = useState(false);

  useEffect(() => {
    startTransition(() => {
      setCurrentImagePath(initialImagePath);
    });
  }, [initialImagePath]);

  useEffect(() => {
    startTransition(() => {
      setCurrentPanoramaId(stepId);
    });
  }, [stepId]);

  useEffect(() => {
    if (isSmallScreen || isMediumScreen) {
      setLocalIsFullscreen(true);
      setIsFullscreen(true);
    } else {
      setLocalIsFullscreen(false);
      setIsFullscreen(false);
    }
  }, [isSmallScreen, isMediumScreen, setIsFullscreen]);

  useEffect(() => {}, [stepId]);

  const handleThumbnailClick = (path: string) => {
    const nextId = parseInt(path, 10);

    if (nextId !== currentPanoramaId) {
      startTransition(() => {
        setCurrentPanoramaId(nextId);
        const nextImagePath = prefetchedContent[nextId] || initialImagePath;

        setCurrentImagePath(nextImagePath);
        handleStepUpdate(nextId);
      });
    }
  };

  const handleExitFullscreen = () => {
    setLocalIsFullscreen(false);
    setIsFullscreen(false);
  };

  const handleEnterFullscreen = () => {
    setLocalIsFullscreen(true);
    setIsFullscreen(true);
  };

  const texture = useLoader(TextureLoader, currentImagePath);

  return (
    <div className="fixed inset-0">
      {isFullscreen ? (
        <button
          onClick={handleExitFullscreen}
          className="absolute top-4 left-4 z-50 p-2 bg-gray-800 text-white rounded-full"
        >
          <CloseIcon />
        </button>
      ) : (
        <button
          onClick={handleEnterFullscreen}
          className="absolute top-4 left-4 z-50 p-2 bg-gray-800 text-white rounded-full"
        >
          <FullscreenIcon />
        </button>
      )}
      <Suspense fallback={<div>Loading panorama...</div>}>
        <Canvas className="absolute inset-0 w-full h-full">
          <ambientLight />
          <Sky />
          <mesh>
            <sphereGeometry attach="geometry" args={[500, 60, 40]} />
            <meshBasicMaterial attach="material" map={texture} side={2} />
          </mesh>
          {clickToPaths &&
            clickToPaths.map((thumbnail, index) => {
              return (
                <Thumbnail
                  key={`${thumbnail.path}-${thumbnail.angle}`}
                  path={thumbnail.path.toString()}
                  angle={thumbnail.angle}
                  onClick={handleThumbnailClick}
                />
              );
            })}
          <OrbitControls />
        </Canvas>
      </Suspense>
    </div>
  );
};

export default PanoramaViewer;
